import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { useNavigate, useLocation, createSearchParams } from "react-router-dom";
import { useUserContext } from "../../context/useUserContext";
import { showErrorNotification } from "../../helper/Notifications";
import PrimaryButton from "../Generic/PrimaryButton";

const OTPVerificationCard = ({ setIsLoading }) => {
  const { loginUser } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const email = location.state?.email;
  const password = location.state?.password;
  const backTo = location.state?.backTo;
  const backToSearch = location.state?.backToSearch;

  useEffect(() => {
    // Redirect to login if no email/password in state
    if (!email || !password) {
      navigate("/login");
    }
  }, [email, password, navigate]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, []);

  const handleInputChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6).split("");

    const newOtpValues = [...otpValues];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newOtpValues[index] = digit;
      }
    });
    setOtpValues(newOtpValues);

    // Focus the next empty input or last input
    const nextEmptyIndex = newOtpValues.findIndex(val => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs[focusIndex].current?.focus();
  };

  const handleConfirm = () => {
    const otpCode = otpValues.join("");

    if (otpCode.length !== 6) {
      showErrorNotification("Please enter a complete 6-digit code");
      return;
    }

    setIsLoading(true);

    loginUser(
      email,
      password,
      otpCode,
      function (response) {
        setIsLoading(false);

        if (backTo && backTo !== "/") {
          navigate({
            pathname: backTo,
            search: createSearchParams(backToSearch).toString(),
          });
        } else {
          navigate("/dashboard");
        }
      },
      function (error) {
        if (error.response?.data?.error === "Invalid OTP code") {
          showErrorNotification("Your OTP code is incorrect!");
        } else if (error.response?.data?.error === "Invalid email or password") {
          showErrorNotification("Your password is incorrect!");
        } else if (error.response?.data?.error === "invalid_grant") {
          showErrorNotification("Provided credentials are invalid");
        } else {
          showErrorNotification("Authentication failed. Please try again.");
        }
        setIsLoading(false);
      }
    );
  };

  const handleCancel = () => {
    navigate("/login");
  };

  const isComplete = otpValues.every(val => val !== "");

  return (
    <Card>
      <TopSection>
        <IconWrapper>
          <IconInner>
            <WarningIcon>
              <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.9998 8.50021V12.5002M11.9998 16.5002H12.0098M10.2898 3.3602L1.81978 17.5002C1.64514 17.8026 1.55274 18.1455 1.55177 18.4947C1.55079 18.8439 1.64127 19.1873 1.8142 19.4907C1.98714 19.7941 2.2365 20.047 2.53748 20.2241C2.83847 20.4012 3.18058 20.4964 3.52978 20.5002H20.4698C20.819 20.4964 21.1611 20.4012 21.4621 20.2241C21.7631 20.047 22.0124 19.7941 22.1854 19.4907C22.3583 19.1873 22.4488 18.8439 22.4478 18.4947C22.4468 18.1455 22.3544 17.8026 22.1798 17.5002L13.7098 3.3602C13.5315 3.06631 13.2805 2.82332 12.981 2.65469C12.6814 2.48605 12.3435 2.39746 11.9998 2.39746C11.656 2.39746 11.3181 2.48605 11.0186 2.65469C10.7191 2.82332 10.468 3.06631 10.2898 3.3602Z" stroke="#DC6803" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </WarningIcon>
          </IconInner>
        </IconWrapper>

        <TitleContainer>
          <h1>Authentication Code</h1>
          <p>Enter the 6-digit code from your authenticator app to complete sign-in.</p>
        </TitleContainer>
      </TopSection>

      <InputsWrapper>
        <Label>Verification code</Label>
        <OTPInputsContainer>
          {otpValues.slice(0, 3).map((value, index) => (
            <OTPInput
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={value}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
            />
          ))}
          <Separator>-</Separator>
          {otpValues.slice(3, 6).map((value, index) => (
            <OTPInput
              key={index + 3}
              ref={inputRefs[index + 3]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={value}
              onChange={(e) => handleInputChange(index + 3, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index + 3, e)}
              onPaste={handlePaste}
            />
          ))}
        </OTPInputsContainer>
      </InputsWrapper>

      <ButtonsWrapper>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
        <PrimaryButton
          styled={{ width: "100%", flex: 1, height: "44px" }}
          text={"Confirm"}
          onClick={handleConfirm}
          disabled={!isComplete}
        />
      </ButtonsWrapper>
    </Card>
  );
};

export default OTPVerificationCard;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  align-items: flex-start;
  max-width: 480px;
  width: 100%;
  background: ${lightThemeColors.background};
  gap: 24px;
  border-radius: 12px;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 650px) {
    width: 100%;
    padding: 30px 20px;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFAEB;
  border-radius: 50%;
`;

const IconInner = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FEF0C7;
  border-radius: 50%;
`;

const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;

  h1 {
    color: ${lightThemeColors.darkColor};
    font-weight: 600;
    font-size: 18px;
    line-height: 28px;
    margin: 0;
  }

  p {
    color: rgba(102, 102, 102, 1);
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }
`;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Label = styled.label`
  color: ${lightThemeColors.darkColor};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

const OTPInputsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const OTPInput = styled.input`
  width: 56px;
  height: 56px;
  border: 1px solid rgba(221, 221, 221, 1);
  border-radius: 8px;
  text-align: center;
  font-size: 24px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};
  background: ${lightThemeColors.background};
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${lightThemeColors.primaryColor};
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }

  &::placeholder {
    color: rgba(204, 204, 204, 1);
  }

  @media (max-width: 650px) {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
`;

const Separator = styled.span`
  color: rgba(153, 153, 153, 1);
  font-size: 24px;
  font-weight: 400;
  margin: 0 4px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: ${lightThemeColors.background};
  border: 1px solid rgba(221, 221, 221, 1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  color: ${lightThemeColors.darkColor};
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(250, 250, 250, 1);
    border-color: rgba(200, 200, 200, 1);
  }

  &:active {
    transform: scale(0.98);
  }
`;
